import { ALL_DISPUTE_REASONS, type DisputeReason } from '../../i18n/dispute-reasons.js';

export interface MotifCategory {
  id: string;
  /** FR header rendered above the motif list. */
  label: string;
  reasons: DisputeReason[];
}

/**
 * Editorial 5-bucket grouping over Iopole's 28 DISPUTED-allowed motifs,
 * per REQ-FE-018 + `feedback_dispute_motifs_full`. The contract enum
 * carries no category metadata, so this mapping is the single source of
 * truth in the FE.
 *
 * Every motif is assigned to exactly one category — guarded by the
 * coverage test in `__tests__/DisputeModal.test.tsx`. Unmapped motifs
 * fall back to "Autres" at render-time (defensive — should never fire
 * once the coverage test passes).
 */
// REQ-FE-033 area B7: 7-bucket grouping ported verbatim from the
// mockup source at
// `.claude/worktrees/feat+web-vite-react-scaffold/apps/web/src/lib/dispute-reasons.ts`
// — same category order, same labels, same motif → category mapping.
// Coverage is still all 28 contract motifs assigned exactly once
// (enforced by the coverage test).
export const DISPUTE_MOTIF_CATEGORIES: MotifCategory[] = [
  {
    id: 'amounts',
    label: 'Montants & calculs',
    reasons: [
      'VAT_RATE_INCORRECT',
      'TOTAL_AMOUNT_INCORRECT',
      'CALCULATION_ERROR',
      'UNIT_PRICE_INCORRECT',
      'PRICES_INCORRECT',
      'DISCOUNT_INCORRECT',
      'FINANCE_TERMS_INCORRECT',
    ],
  },
  {
    id: 'references',
    label: 'Références & bon de commande',
    reasons: [
      'PO_REFERENCE_INCORRECT_OR_MISSING',
      'CONTRACT_REFERENCE_MISSING',
      'REFERENCE_INCORRECT',
      'SIRET_INCORRECT_OR_MISSING',
      'ROUTING_CODE_INCORRECT',
    ],
  },
  {
    id: 'recipient',
    label: 'Destinataire & émetteur',
    reasons: [
      'INCORRECT_RECIPIENT',
      'EINVOICING_ADDRESS_INCORRECT',
      'UNKNOWN_ISSUER',
      'UNKNOWN_TRANSACTION',
    ],
  },
  {
    id: 'delivery',
    label: 'Livraison & articles',
    reasons: [
      'QUANTITY_INCORRECT',
      'ITEM_INCORRECT',
      'ITEM_QUALITY_INSUFFICIENT',
      'DELIVERY_ISSUE',
    ],
  },
  {
    id: 'duplicates',
    label: 'Doublons & contrat',
    reasons: ['DUPLICATE_INVOICE', 'DOUBLE_INVOICING', 'CONTRACT_TERMINATED'],
  },
  {
    id: 'banking',
    label: 'Coordonnées bancaires & paiement',
    reasons: ['BANK_DETAILS_INCORRECT', 'PAYMENT_TERMS_INCORRECT'],
  },
  {
    id: 'other',
    label: 'Autre',
    reasons: ['LEGAL_INFORMATION_MISSING', 'MISSING_DOCUMENTS', 'OTHER'],
  },
];

/** Build a Set of every motif assigned to some category (for coverage tests + render fallback). */
export function categorisedReasons(): Set<DisputeReason> {
  const out = new Set<DisputeReason>();
  for (const c of DISPUTE_MOTIF_CATEGORIES) {
    for (const r of c.reasons) out.add(r);
  }
  return out;
}

/** Returns true iff every contract DisputeReason landed in exactly one category. */
export function isCoverageComplete(): boolean {
  const seen = categorisedReasons();
  if (seen.size !== ALL_DISPUTE_REASONS.length) return false;
  return ALL_DISPUTE_REASONS.every((r) => seen.has(r));
}
